import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingUp, Download, FileText as FileTextIcon, Users, FolderKanban, DollarSign, FileText, Wallet, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

const PRESET_RANGES = [
  { key: 'all', label: 'All Time' },
  { key: 'ytd', label: 'YTD' },
  { key: 'last_90', label: 'Last 90 days' },
  { key: 'last_12mo', label: 'Last 12 mo' },
  { key: 'custom', label: 'Custom' },
];

function rangeFor(preset) {
  const today = todayIso();
  const d = new Date();
  if (preset === 'ytd') return { start: `${d.getFullYear()}-01-01`, end: today };
  if (preset === 'last_90') {
    d.setDate(d.getDate() - 90);
    return { start: d.toISOString().slice(0, 10), end: today };
  }
  if (preset === 'last_12mo') {
    d.setFullYear(d.getFullYear() - 1);
    return { start: d.toISOString().slice(0, 10), end: today };
  }
  return { start: '', end: '' };
}

const METRIC_META = {
  contract_value: { label: 'Total Contract Value', icon: FileText, source: 'Contracts (excludes Inactive)' },
  invoiced: { label: 'Total Invoiced', icon: DollarSign, source: 'Invoices (excludes Cancelled)' },
  collected: { label: 'Amount Paid / Collected', icon: Wallet, source: 'Invoice Payments (excludes Cancelled)' },
};

export function RevenueConcentrationCard() {
  const [groupBy, setGroupBy] = useState('client');
  const [metric, setMetric] = useState('invoiced');
  const [rangePreset, setRangePreset] = useState('ytd');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const { start, end } = useMemo(() => {
    if (rangePreset === 'custom') return { start: customStart, end: customEnd };
    return rangeFor(rangePreset);
  }, [rangePreset, customStart, customEnd]);

  
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ group_by: groupBy, metric });
    if (start) params.set('start_date', start);
    if (end) params.set('end_date', end);
    api.get(`/accounting/revenue-concentration?${params.toString()}`)
      .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(e => {
        if (cancelled) return;
        setData(null);
        setLoading(false);
        // Surface backend validation messages (e.g. "start_date must be on or before end_date")
        toast.error(e.response?.data?.detail || 'Failed to load revenue concentration');
      });
    return () => { cancelled = true; };
  }, [groupBy, metric, start, end]);
  

  const [exportingPdf, setExportingPdf] = useState(false);

  const exportCsv = () => {
    if (!data?.rows?.length) return toast.error('Nothing to export');
    const groupLabel = groupBy === 'client' ? 'Client' : 'Project';
    const metricLabel = METRIC_META[metric].label;
    const header = ['Rank', groupLabel, metricLabel, 'Records', 'Share %', 'Cumulative %', 'In 80% Cutoff'];
    const lines = [header.join(',')];
    data.rows.forEach((r, i) => {
      const safeName = `"${(r.name || '').replace(/"/g, '""')}"`;
      lines.push([i + 1, safeName, r.value.toFixed(2), r.count, r.share_pct.toFixed(2), r.cumulative_pct.toFixed(2), r.is_in_80_cutoff ? 'Yes' : 'No'].join(','));
    });
    lines.push(['', 'TOTAL', data.total_value.toFixed(2), '', '100.00', '100.00', ''].join(','));
    // BOM + CRLF for Excel-on-Windows + Unicode client names compatibility
    const csv = '\uFEFF' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const range = (start || end) ? `_${start || 'all'}_to_${end || 'now'}` : '';
    a.download = `revenue-concentration_${groupBy}_${metric}${range}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
    toast.success('CSV downloaded');
  };

  const exportPdf = async () => {
    if (!data?.rows?.length) return toast.error('Nothing to export');
    setExportingPdf(true);
    try {
      const params = new URLSearchParams({ group_by: groupBy, metric });
      if (start) params.set('start_date', start);
      if (end) params.set('end_date', end);
      const r = await api.get(`/accounting/revenue-concentration/export-pdf?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const range = (start || end) ? `_${start || 'all'}_to_${end || 'now'}` : '';
      a.download = `revenue-concentration_${groupBy}_${metric}${range}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF export failed');
    }
    setExportingPdf(false);
  };

  const MetricIcon = METRIC_META[metric].icon;
  const cutoffIdx = data?.cutoff_index_80; // 0-based, first row whose cumulative ≥ 80%

  return (
    <Card className="border-border bg-card shadow-none" data-testid="revenue-concentration-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-heading flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-violet-400" /> Revenue Concentration</span>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportPdf} disabled={!data?.rows?.length || exportingPdf} data-testid="revenue-export-pdf">
              <FileTextIcon className="h-3 w-3 mr-1" /> {exportingPdf ? 'Building PDF...' : 'Export PDF'}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportCsv} disabled={!data?.rows?.length} data-testid="revenue-export-csv">
              <Download className="h-3 w-3 mr-1" /> Export CSV
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Group By</Label>
            <div className="flex gap-1 mt-1" data-testid="revenue-group-by">
              {[
                { key: 'client', label: 'Client', icon: Users },
                { key: 'project', label: 'Project', icon: FolderKanban },
              ].map(o => (
                <button
                  key={o.key}
                  onClick={() => setGroupBy(o.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition-colors ${groupBy === o.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
                  data-testid={`revenue-group-${o.key}`}
                >
                  <o.icon className="h-3 w-3" /> {o.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Metric</Label>
            <div className="flex flex-col gap-1 mt-1" data-testid="revenue-metric">
              {Object.entries(METRIC_META).map(([k, m]) => {
                const I = m.icon;
                return (
                  <button
                    key={k}
                    onClick={() => setMetric(k)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors text-left ${metric === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
                    data-testid={`revenue-metric-${k}`}
                  >
                    <I className="h-3 w-3 shrink-0" /> {m.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date Range</Label>
            <div className="flex flex-wrap gap-1 mt-1" data-testid="revenue-range-presets">
              {PRESET_RANGES.map(p => (
                <button
                  key={p.key}
                  onClick={() => setRangePreset(p.key)}
                  className={`px-2.5 py-1 rounded-md border text-xs transition-colors ${rangePreset === p.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
                  data-testid={`revenue-range-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {rangePreset === 'custom' && (
              <div className="flex gap-1.5 mt-2" data-testid="revenue-custom-range">
                <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="h-7 text-xs bg-background border-border" data-testid="revenue-custom-start" />
                <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="h-7 text-xs bg-background border-border" data-testid="revenue-custom-end" />
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-secondary/20 border border-border">
          <div className="flex items-center gap-2 text-xs">
            <MetricIcon className="h-3.5 w-3.5 text-violet-400" />
            <span className="text-muted-foreground">{METRIC_META[metric].label} · {groupBy === 'client' ? 'by Client' : 'by Project'}{start || end ? ` · ${start || '∞'} → ${end || 'now'}` : ' · All Time'}</span>
          </div>
          {data && (
            <div className="flex items-center gap-3 text-xs">
              <span className="text-muted-foreground">{data.total_records} {groupBy}{data.total_records !== 1 ? 's' : ''}</span>
              <span className="font-heading font-bold tabular-nums" data-testid="revenue-total">{fmtMoney(data.total_value)}</span>
            </div>
          )}
        </div>

        {/* Pareto cutoff hint */}
        {data && cutoffIdx !== null && cutoffIdx !== undefined && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-violet-500/20 bg-violet-500/5">
            <AlertCircle className="h-3.5 w-3.5 text-violet-400 mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground">
              Top <span className="font-semibold text-violet-300">{cutoffIdx + 1}</span> {groupBy}{cutoffIdx === 0 ? '' : 's'} drive <span className="font-semibold text-violet-300">80%</span> of {METRIC_META[metric].label.toLowerCase()} ({data.rows.slice(0, cutoffIdx + 1).reduce((s, r) => s + r.share_pct, 0).toFixed(1)}%).
              {data.total_records > cutoffIdx + 1 && <> The remaining {data.total_records - cutoffIdx - 1} contribute the other 20%.</>}
            </p>
          </div>
        )}

        {/* Table */}
        <div className="border border-border rounded-md overflow-hidden">
          <div className="grid grid-cols-12 px-3 py-2 bg-secondary/30 text-xs uppercase tracking-wider text-muted-foreground">
            <div className="col-span-1">#</div>
            <div className="col-span-5">{groupBy === 'client' ? 'Client' : 'Project'}</div>
            <div className="col-span-2 text-right">{METRIC_META[metric].label}</div>
            <div className="col-span-1 text-right">Recs</div>
            <div className="col-span-1 text-right">Share</div>
            <div className="col-span-2 text-right">Cumulative</div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</div>
            ) : !data?.rows?.length ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">No records match the selected filters.</div>
            ) : (
              data.rows.map((r, i) => {
                const inCutoff = r.is_in_80_cutoff;
                return (
                  <div
                    key={r.id || i}
                    className={`grid grid-cols-12 px-3 py-2 text-xs border-t border-border ${inCutoff ? 'bg-violet-500/5' : ''}`}
                    data-testid={`revenue-row-${i}`}
                  >
                    <div className="col-span-1 font-mono text-muted-foreground">{i + 1}</div>
                    <div className="col-span-5 truncate flex items-center gap-1.5" title={r.name}>
                      {inCutoff && <span className="h-1.5 w-1.5 rounded-full bg-violet-400 shrink-0" title="In top 80%" />}
                      <span className={inCutoff ? 'text-foreground font-medium' : 'text-muted-foreground'}>{r.name || '(Unknown)'}</span>
                    </div>
                    <div className="col-span-2 text-right tabular-nums font-medium">{fmtMoney(r.value)}</div>
                    <div className="col-span-1 text-right tabular-nums text-muted-foreground">{r.count}</div>
                    <div className="col-span-1 text-right tabular-nums text-muted-foreground">{r.share_pct.toFixed(1)}%</div>
                    <div className="col-span-2 text-right tabular-nums">
                      <span className={inCutoff ? 'text-violet-300' : 'text-muted-foreground'}>{r.cumulative_pct.toFixed(1)}%</span>
                      {/* Mini cumulative bar */}
                      <div className="h-0.5 mt-0.5 bg-secondary/40 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${inCutoff ? 'bg-violet-400' : 'bg-zinc-500/40'}`}
                          style={{ width: `${Math.min(100, r.cumulative_pct)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground italic">
          Source: {METRIC_META[metric].source}. Pareto 80% cutoff is highlighted in violet. Cancelled invoices are always excluded.
        </p>
      </CardContent>
    </Card>
  );
}
